import type { LegalDoc } from '../types/legal'
import { BRAND, CONTACT, FORM_RESPONSE_TIME, LEGAL_ENTITY } from './site'

/**
 * The four legal documents, one per URL at /legal/<slug>.
 *
 * WRITTEN TO BE TRUE OF THIS SITE AS BUILT. Every factual claim here is
 * checkable in the code:
 * - the only two collection points are the event booking form and the shop
 *   reservation form, and their field lists below match the forms exactly,
 * - no analytics, advertising or tracking script is loaded anywhere,
 * - the one third-party request the browser makes is to Google Fonts, for
 *   Bebas Neue, which `index.css` imports on line 1, and
 * - nothing on the site takes payment.
 *
 * Where a claim depends on something outside the code, it is stated as a
 * category rather than invented as a fact: "our hosting provider" rather
 * than a named vendor we have not confirmed.
 *
 * Entity details confirmed by the client 2026-09-12: a sole trader,
 * Andrew Martin trading as Low On Fuel Café, operating from the café
 * address. L01 is closed.
 *
 * The client has confirmed 2026-09-12 that these documents have been
 * reviewed and are accepted as adequate for a simple marketing site with
 * no online payment. I12 is closed on that basis. If the site ever takes
 * payment, sells goods for delivery, or starts handling booking data,
 * the cancellation, refund and liability clauses need looking at again —
 * a distance-selling contract is a different animal to this.
 */

/**
 * The business is a sole trader, so the entity is a person trading under
 * a name — never a registered company with a number. Written as
 * "Andrew Martin, trading as Low On Fuel Café", which is the form the
 * Business Names provisions of the Companies Act 2006 expect.
 */
const ENTITY = `${LEGAL_ENTITY.proprietorName}, trading as ${LEGAL_ENTITY.tradingName}`
const EMAIL = CONTACT.email
const PHONE = CONTACT.telephone
const UPDATED = '12 September 2026'

export const privacyDoc: LegalDoc = {
  id: 'L-PRIV',
  slug: 'privacy',
  title: 'Privacy Policy',
  shortLabel: 'Privacy',
  summary:
    'What we collect when you book an event or reserve an item, why, how long we keep it, and the rights you hold.',
  description: `How ${BRAND.name} collects, uses and looks after personal data under the UK GDPR and the Data Protection Act 2018, and the rights you hold over it.`,
  lastUpdated: UPDATED,
  intro: `This policy covers the ${BRAND.domain} website only. It explains what personal data we collect through it, why we collect it, how long we keep it and what you can ask us to do with it. We have tried to write it so that you could actually finish it.`,
  sections: [
    {
      heading: 'Who we are',
      body: `${ENTITY} is the data controller for the personal data described here. We are a sole trader rather than a limited company, so there is no company number: the person responsible is ${LEGAL_ENTITY.proprietorName}, and the business address is ${LEGAL_ENTITY.businessAddress}. For anything at all to do with your data, including any of the requests set out below, email ${EMAIL} or ring us on ${PHONE}.`,
      highlight: [
        'the data controller',
        'a sole trader rather than a limited company',
        EMAIL,
        PHONE,
      ],
    },
    {
      heading: 'What we collect, and when',
      body: 'We collect only what you hand us. There is no account to create, nothing is sold on this website, and we do not collect anything at all from you simply reading a page.',
      items: [
        'Event booking form, where an event needs booking: your first and last name, email address, telephone number, the number of places you want, and, for a guided ride, your motorcycle make, model and registration. Where a meal is included we also record whether you want it.',
        'Shop reservation form: your first and last name, email address and the message you write. The message field is yours to fill as you like, so please keep health information, financial details and anything else sensitive out of it.',
        'Ringing or emailing us: if you get in touch directly we keep that correspondence and the contact details in it.',
        'Server logs: our hosting keeps ordinary technical records of requests to the site, including the requesting IP address, which page was asked for, when, and the browser and device. These are produced by the act of serving a web page and are used for security and for working out what broke.',
      ],
      highlight: [
        'only what you hand us',
        'motorcycle make, model and registration',
        'keep health information, financial details and anything else sensitive out of it',
        'we do not collect anything at all from you simply reading a page',
      ],
    },
    {
      heading: 'Why we use it, and our lawful basis',
      body: 'The UK GDPR requires a lawful basis for each purpose. Ours are as follows.',
      items: [
        'To hold your place at an event and get back to you about it. Basis: legitimate interests, being our shared interest in answering a booking you deliberately sent. Where you have paid, the basis becomes performance of a contract.',
        'To manage parking and marshalling on ride days, which is why we ask for your motorcycle registration. Basis: legitimate interests in running a safe car park and being able to identify a machine that needs moving.',
        'To tell you whether a shop item is still available and to arrange collection. Basis: legitimate interests, and then performance of a contract once you agree to buy it.',
        'To keep the site working, available and secure, including investigating faults and misuse. Basis: legitimate interests in protecting our own systems and the people using them.',
        'To keep the accounting records we are obliged to keep once you have paid us for something. Basis: legal obligation.',
      ],
      highlight: [
        'Basis: legitimate interests',
        'Basis: performance of a contract',
        'Basis: legal obligation',
        'your motorcycle registration',
      ],
    },
    {
      heading: 'Your motorcycle registration',
      /* Flagged in the registry before this policy existed: a registration
         identifies a keeper through the DVLA, so it is personal data and
         cannot sit in a form unmentioned. It gets its own clause because a
         rider is entitled to know why a café is asking for it. */
      body: 'A registration number identifies the keeper of a vehicle through the DVLA, so it counts as personal data and we treat it as such. We ask for it on guided rides so we can marshal the car park, account for everyone on a ride-out and get hold of you if your machine needs moving. We do not run checks against it, we do not pass it to anyone, and we delete it once the ride it relates to has happened.',
      highlight: [
        'counts as personal data',
        'We do not run checks against it, we do not pass it to anyone',
        'we delete it once the ride it relates to has happened',
      ],
    },
    {
      heading: 'What we do not do',
      body: 'A privacy policy is more useful for what it rules out than for what it permits. On this website:',
      items: [
        'We do not sell, rent or trade your personal data. There are no circumstances in which we would.',
        'We load no analytics, no advertising and no tracking scripts of any kind. There is nothing to opt out of, because nothing is running.',
        'We do not build a profile of you, and nothing here makes an automated decision about you.',
        'We do not add you to a mailing list because you booked an event or reserved an item. Neither form signs you up to anything.',
        'We do not ask for special category data, and we would rather you did not send us any.',
      ],
      highlight: [
        'We do not sell, rent or trade your personal data',
        'no analytics, no advertising and no tracking scripts of any kind',
        'nothing is running',
        'Neither form signs you up to anything',
      ],
    },
    {
      heading: 'Who else sees it',
      body: 'We do not share your data with anyone who wants it for their own ends. We do rely on service providers who process it on our instructions and cannot use it for anything else, under a written contract as the UK GDPR requires. There are three: the company that hosts this website, the company that provides our email, and, for paid events, the payment provider that takes the money. We would also disclose data where the law requires it, for instance on a valid request from a court, the police or a regulator.',
      highlight: [
        'anyone who wants it for their own ends',
        'cannot use it for anything else',
      ],
    },
    {
      heading: 'Where it is held',
      body: 'We aim to keep personal data in the United Kingdom or the European Economic Area. Where a provider we depend on processes it elsewhere, the transfer is covered by one of the safeguards UK law allows, such as the International Data Transfer Agreement or the UK Addendum to the standard contractual clauses. Ask us which providers are involved and where they sit, and we will tell you.',
    },
    {
      heading: 'How long we keep it',
      body: 'We do not keep personal data indefinitely and we do not keep it just in case.',
      items: [
        'Event bookings: for the event, and then 12 months afterwards so we can deal with any question that comes up about it.',
        'Motorcycle registrations: deleted once the ride has taken place.',
        'Shop reservations that do not lead to a sale: up to 6 months from your last message, then deleted.',
        'Anything that becomes a sale: 6 years after the transaction, which is the period we must keep records for tax purposes.',
        'General correspondence: up to 12 months from your last message.',
        'Server logs: kept by our hosting provider on a short rolling basis for security and diagnostics. Not archived.',
      ],
      highlight: [
        'we do not keep it just in case',
        'deleted once the ride has taken place',
        '6 years after the transaction',
      ],
    },
    {
      heading: 'The rights you hold',
      body: 'Under the UK GDPR you can ask us to do any of the following, and we will answer within one month. There is no charge, and you do not need to explain why you are asking.',
      items: [
        'See it. Ask for a copy of the personal data we hold about you.',
        'Correct it. Have anything inaccurate put right, or anything incomplete filled in.',
        'Delete it. Have it erased where we no longer need it for the purpose we collected it for.',
        'Restrict it. Have us stop using it while a dispute about its accuracy or our basis is sorted out.',
        'Object. Object to our processing where we rely on legitimate interests, and we will stop unless we can show a compelling reason not to.',
        'Take it with you. Receive it in a portable format where we hold it on the basis of consent or a contract.',
      ],
      highlight: ['within one month', 'There is no charge'],
    },
    {
      heading: 'Complaints',
      body: `If you are unhappy with how we have handled your data, please tell us first at ${EMAIL} and we will try to put it right. You also have the right to complain to the Information Commissioner's Office, which regulates data protection in the UK, at ico.org.uk or on 0303 123 1113. You can go to them without coming to us first.`,
      highlight: [
        'ico.org.uk',
        '0303 123 1113',
        'You can go to them without coming to us first',
      ],
    },
    {
      heading: 'Changes to this policy',
      body: `We will update this page when what we do changes, and the date at the top will change with it. Where a change materially affects you, we will say so on the page rather than quietly editing it.`,
    },
  ],
}

export const cookiesDoc: LegalDoc = {
  id: 'L-COOK',
  slug: 'cookies',
  title: 'Cookie Policy',
  shortLabel: 'Cookies',
  summary:
    'This site sets no cookies, which is why you have not been asked to accept any.',
  description: `${BRAND.name} sets no cookies and loads no analytics or tracking scripts. This page explains that, and the one third-party request the site does make.`,
  lastUpdated: UPDATED,
  intro:
    'The short version: this website sets no cookies at all, which is why you have not been shown a banner asking you to accept any. The rest of this page explains what that means and what the site does do.',
  sections: [
    {
      heading: 'We set no cookies',
      /* The reason there is no consent banner, and the reason one would be
         required the moment this stops being true. See registry D02. */
      body: 'There are no analytics cookies, no advertising cookies, no social media cookies and no preference cookies on this site. Nothing is stored on your device to recognise you, count you or follow you elsewhere. This is also why you have not been asked to consent to anything: under the Privacy and Electronic Communications Regulations, consent is required for non-essential cookies, and we have none to ask about. A banner with nothing behind it would be theatre.',
      highlight: [
        'no analytics cookies, no advertising cookies, no social media cookies and no preference cookies',
        'we have none to ask about',
        'A banner with nothing behind it would be theatre',
      ],
    },
    {
      heading: 'The one outside request the site makes',
      body: 'This site is set in Bebas Neue, which it loads from Google Fonts. Your browser therefore makes one request to Google to fetch the font file, and that request includes your IP address, as any web request does. Google Fonts sets no cookie and receives nothing you type into either form. We name it because it is the only third party involved and it is verifiable from the site itself.',
      highlight: [
        'Bebas Neue',
        'Google Fonts',
        'sets no cookie and receives nothing you type',
      ],
    },
    {
      heading: 'What your browser does on its own',
      body: 'Your browser may keep its own record of pages you have visited and files it has already downloaded, so the site loads faster next time. That is your browser doing its job rather than us doing anything, we cannot see it, and clearing your browsing data removes it.',
      highlight: ['That is your browser doing its job rather than us'],
    },
    {
      heading: 'If this changes',
      body: `If we ever add anything that sets a non-essential cookie, such as an analytics tool, an embedded map or a social media feed, we will have to ask your permission before it loads, and we will update this page to say exactly what it is and what it does. We are not going to add one quietly. If you want to check, email ${EMAIL} and ask.`,
      highlight: [
        'we will have to ask your permission before it loads',
        'We are not going to add one quietly',
      ],
    },
  ],
}

export const termsDoc: LegalDoc = {
  id: 'L-TERM',
  slug: 'terms',
  title: 'Terms of Use',
  shortLabel: 'Terms',
  summary:
    'The terms for using this website, booking an event through it and reserving something from the shop.',
  description: `The terms on which you may use the ${BRAND.domain} website, book an event and reserve an item from the shop, including cancellations, refunds and the limits of what we promise.`,
  lastUpdated: UPDATED,
  intro: `These terms cover your use of ${BRAND.domain}, any event you book through it and anything you reserve from the shop. Using the site means accepting them. They do not affect your statutory rights as a consumer, which nothing on this page can take away.`,
  sections: [
    {
      heading: 'Using this website',
      body: `${ENTITY} owns and runs this site. You are welcome to read it, print pages from it and share links to it. Please do not copy our photographs, text or branding for your own commercial use, try to break or overload the site, or scrape it. We may change or withdraw any part of the site at any time.`,
      highlight: ['do not copy our photographs, text or branding'],
    },
    {
      heading: 'Booking an event',
      body: 'Submitting the booking form is a request, not a confirmed place. Your place exists once we have emailed you to confirm it.',
      items: [
        `We aim to reply ${FORM_RESPONSE_TIME}.`,
        'Places are limited and are confirmed in the order the requests reach us.',
        'For a paid event, your place is held once payment has been taken.',
        'Friday Bike Night needs no booking at all. Just turn up.',
      ],
      highlight: [
        'a request, not a confirmed place',
        'once we have emailed you to confirm it',
        'Just turn up',
      ],
    },
    {
      heading: 'Cancelling an event booking',
      body: 'Plans change and bikes break, so this is meant to be straightforward.',
      items: [
        'Tell us at least 48 hours before a paid event and we will refund you in full.',
        'Inside 48 hours we cannot refund the meal, because it will already have been ordered, but we will refund the rest.',
        'If we cancel, for weather or anything else, you get a full refund or a place on the next one, whichever you prefer.',
        'Free events need no cancelling, though it is a kindness to let us know if you were expected.',
      ],
      highlight: [
        'at least 48 hours',
        'refund you in full',
        'you get a full refund or a place on the next one',
      ],
    },
    {
      heading: 'Riding with us',
      body: 'A guided ride is a group of people riding their own motorcycles on public roads. It is not a race, a track day or a tour we are conducting on your behalf, and we are not responsible for how you ride.',
      items: [
        'You are responsible for your own machine being roadworthy, taxed, insured and MOT tested.',
        'You ride to the road conditions and to your own ability, and you obey the law. A marshal picking a route does not change who is riding your bike.',
        'You wear a helmet, as the law requires, and we would strongly encourage proper protective kit.',
        'Ride leaders may ask someone to leave a ride if they are riding in a way that puts other people at risk.',
      ],
      highlight: [
        'It is not a race, a track day or a tour',
        'we are not responsible for how you ride',
        'who is riding your bike',
      ],
    },
    {
      heading: 'Route files',
      body: 'Where we make a GPX or ITN file available for a ride, it is a convenience and nothing more. Roads close, surfaces change and satellite navigation is confidently wrong from time to time. Use your own judgement and the road in front of you rather than the file.',
      highlight: [
        'a convenience and nothing more',
        'Use your own judgement and the road in front of you',
      ],
    },
    {
      heading: 'Reserving from the shop',
      body: 'The shop is not a checkout. Nothing on this site takes your money, and reserving an item does not buy it.',
      items: [
        'Most of what we sell is second-hand and one-off, so two people genuinely cannot have the same piece. The reservation form is how we avoid promising it twice.',
        `Sending the form holds nothing automatically. We will reply ${FORM_RESPONSE_TIME} to say whether it is still here.`,
        'Once we have both agreed, you pay in the café when you collect, or over the telephone if we are posting it.',
        'Prices shown on the site include VAT where it applies. If a price is wrong we will tell you before taking any money, and you are free to walk away.',
      ],
      highlight: [
        'The shop is not a checkout',
        'reserving an item does not buy it',
        'holds nothing automatically',
        'you are free to walk away',
      ],
    },
    {
      heading: 'Second-hand goods and your rights',
      body: 'Second-hand items are sold as they are, and we describe their condition honestly, including the marks and wear that come with age. That does not remove your rights: under the Consumer Rights Act 2015 goods must match their description and be fit for purpose, and if something is not as we described it, tell us and we will refund it. Reclaimed pieces are decorative unless we have said otherwise, so please do not assume a reclaimed part is fit to be fitted to a motorcycle.',
      highlight: [
        'Consumer Rights Act 2015',
        'tell us and we will refund it',
        'decorative unless we have said otherwise',
      ],
    },
    {
      heading: 'The menu and what is in it',
      body: 'Prices and dishes on the Food and Drink page are what we serve at the time of writing, and they change. The prices at the counter are the ones that apply. Our kitchen is small and handles nuts, gluten, dairy and sesame, so we do not claim in writing that any dish is free from an allergen. Please speak to us at the counter before you order, and we will tell you honestly what is in something.',
      highlight: [
        'The prices at the counter are the ones that apply',
        'we do not claim in writing that any dish is free from an allergen',
        'Please speak to us at the counter before you order',
      ],
    },
    {
      heading: 'What we are and are not liable for',
      body: 'We do not limit our liability for death or personal injury caused by our negligence, for fraud, or for anything else the law does not allow us to limit. Beyond that, we are not liable for loss you could not reasonably have expected, for loss of profit or opportunity, or for anything arising from your use of a route file or your own riding. This site is provided as it is: we work hard to keep it accurate but we cannot promise it is free of error or always available.',
      highlight: [
        'death or personal injury caused by our negligence',
        'anything else the law does not allow us to limit',
      ],
    },
    {
      heading: 'Which law applies',
      body: `These terms are governed by the law of ${LEGAL_ENTITY.jurisdiction}, and the courts there have jurisdiction over any dispute. If you live elsewhere in the United Kingdom, you may bring proceedings in your own country.`,
    },
  ],
}

export const accessibilityDoc: LegalDoc = {
  id: 'L-ACCESS',
  slug: 'accessibility',
  title: 'Accessibility Statement',
  shortLabel: 'Accessibility',
  summary:
    'How accessible this website is, what we know is not right yet, and how to tell us when something blocks you.',
  description: `An accessibility statement for ${BRAND.domain}: the standard we build to, what has been tested, the known problems, and how to report one.`,
  lastUpdated: UPDATED,
  intro: `We want everyone to be able to use this site, whether they are using a keyboard, a screen reader, magnification or a phone in the sun outside the café. This statement says how far we have got and, more usefully, where we have not got yet.`,
  sections: [
    {
      heading: 'What we aim for',
      body: 'We build to the Web Content Accessibility Guidelines 2.2 at level AA. That is the standard UK public sector sites are held to, and we think it is a reasonable bar for anyone. Some of the site meets it comfortably and some of it we have not verified, which is the honest position and is set out below.',
      highlight: [
        'Web Content Accessibility Guidelines 2.2 at level AA',
        'some of it we have not verified',
      ],
    },
    {
      heading: 'What should work',
      body: 'The following are built in deliberately rather than by accident, and each has been checked by hand:',
      items: [
        'Everything can be reached and operated with a keyboard alone, in an order that matches the page, with a visible focus outline on every control.',
        'A skip link at the top of each page jumps straight past the navigation to the main content.',
        'Pages use real headings in order, real landmarks and real lists, so a screen reader can navigate by structure instead of reading everything.',
        'Every photograph either has alt text describing what it shows or is marked decorative where the surrounding words already say it.',
        'Both forms have visible labels, mark optional fields in words rather than by an asterisk, and put errors both beside the field and in a summary at the top that takes focus.',
        'Status is never carried by colour alone. Shop stock, for instance, carries a shape and a text label as well.',
        'Text reflows to 320px without horizontal scrolling, and the site works at 200% zoom.',
        'Moving between pages is announced, so someone using a screen reader is not left wondering whether anything happened.',
        'Animation is minimal and honours the reduced motion setting on your device.',
      ],
      highlight: [
        'keyboard alone',
        'never carried by colour alone',
        'honours the reduced motion setting',
      ],
    },
    {
      heading: 'What we know is not right yet',
      /* Kept truthful against the build register rather than aspirational.
         An accessibility statement that claims full conformance is worse
         than one that names its gaps. */
      body: 'We would rather name these than have you find them:',
      items: [
        'The site has not been through a formal independent audit or been tested with a representative group of disabled users. Our checks are our own.',
        'Assistive technology testing has been partial. We have not run the site through every combination of screen reader and browser.',
        'There is no map on the Visit page. The directions are written out instead, which some people will find harder than a map and others easier.',
        'The gallery is a wall of photographs with alt text but no captions on the page, so the descriptions are available to a screen reader but not to someone who simply wants to read them.',
        'Colour contrast has been checked on the palette but not on every single state of every control.',
        'We do not publish documents as PDFs today. If we ever do, they will need their own accessibility work and this statement will need updating.',
      ],
      highlight: [
        'has not been through a formal independent audit',
        'Our checks are our own',
      ],
    },
    {
      heading: 'Telling us about a problem',
      body: `If something on this site stops you doing what you came to do, please tell us. Email ${EMAIL} or ring ${PHONE} and say which page you were on and what happened. We will reply ${FORM_RESPONSE_TIME} and we will not ask you to justify the request. If you need something on this site in another format, or you would rather just book an event or ask about an item over the telephone, ring us and we will sort it out that way.`,
      highlight: [
        'please tell us',
        'we will not ask you to justify the request',
        'ring us and we will sort it out that way',
      ],
    },
    {
      heading: 'The café itself',
      /* Was `body: null`. The interior photographs now support a partial
         answer, so it is better to give what is genuinely known and name
         what is not than to publish nothing. Still deliberately unmeasured:
         a doorway width stated wrongly sends someone on a wasted journey.
         Registry L02. */
      body: 'A statement about our website is only half an answer for a café, so here is the honest half we can give. There are steps up to the main entrance, so the building is not step-free at the door. There is no accessible toilet: the one toilet is unisex and is reached along a corridor from the bar. Inside, the rooms are small and connected by a step or two, some of the doorways are narrow, several ceiling beams are low, and the floors are a mix of flagstones, carpet and boards. We have a sign up asking you to mind your head and the step. The terrace at the front is flagged and the deck is raised. What we have not done is measure any of it, so we are not going to publish widths or step heights we cannot stand behind. The car park is large and level, and if getting inside is the problem we would far rather bring your order out to you than have you struggle — ring us and we will sort it out that way.',
      highlight: [
        'There are steps up to the main entrance',
        'There is no accessible toilet',
        'we would far rather bring your order out to you',
      ],
    },
    {
      heading: 'How this statement was prepared',
      body: `Prepared on ${UPDATED} by the team who built the site, on the basis of our own manual testing against WCAG 2.2 AA. It has not been independently verified. We will review it whenever we make a significant change to the site.`,
      highlight: ['It has not been independently verified'],
    },
  ],
}

/** Order of the document navigation, and of the footer legal bar. */
export const legalDocs: LegalDoc[] = [
  privacyDoc,
  cookiesDoc,
  termsDoc,
  accessibilityDoc,
]

export const getLegalDoc = (slug: string): LegalDoc | undefined =>
  legalDocs.find((doc) => doc.slug === slug)
